import React from 'react';
import { Link } from 'react-router-dom';
import { LogOut, School } from 'lucide-react';
import { InstallMenuItem } from './InstallPrompt';
import PushToggle from './PushToggle';
import { useT } from '../i18n.jsx';

// The shell a student or guardian sees.
//
// Families open four screens and nothing else, so a 280px sidebar listing four links is mostly empty
// space wrapped around a hamburger. The four become the navigation itself: a bottom tab bar on a
// phone -- which is what makes an installed PWA read as an app rather than a website -- and a row
// under the school name on a desktop, where a bar pinned to the bottom of a 1080px-tall window is
// just far away from everything.
//
// It is built for however many tabs are actually visible. module_access can switch any of the four
// off per role, so two tabs and one tab both have to look deliberate; nothing here assumes four.

const NAV_HEIGHT = 62;

export const FamilyTabBar = ({ modules, pathname, isMobile }) => {
  const { t } = useT();
  // One tab is not a choice, so there is nothing to navigate: the top bar already names the school
  // and the screen fills the rest. Rendering a single-item tab bar would be the same fake tab this
  // codebase keeps growing -- a control that looks pressable and does nothing.
  if (modules.length < 2) return null;

  const bar = {
    display: 'flex',
    backgroundColor: 'var(--color-bg-card)',
    flexShrink: 0,
    ...(isMobile
      ? {
        borderTop: '1px solid var(--color-border)',
        // Clears the iPhone home indicator. Resolves to 0px everywhere else, so a browser tab is
        // unaffected. The bar is a flex sibling of <main>, not position:fixed, so no space has to
        // be reserved for it and it can never overlay the content it pushes.
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        boxShadow: '0 -2px 8px rgba(13, 43, 55, 0.06)',
      }
      : {
        borderBottom: '1px solid var(--color-border)',
        padding: '0 1rem',
        gap: '0.25rem',
      }),
  };

  return (
    <nav style={bar} aria-label={t('familyNav.sections')}>
      {modules.map((m) => {
        const Icon = m.icon;
        const active = pathname.startsWith(m.url);
        return (
          <Link
            key={m.id}
            to={m.url}
            aria-current={active ? 'page' : undefined}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textDecoration: 'none',
              color: active ? 'var(--color-primary)' : 'var(--color-text-muted)',
              fontWeight: active ? 600 : 400,
              backgroundColor: active ? 'var(--color-nav-active)' : 'transparent',
              // The rail sits on the edge facing the content, so on a bottom bar it is on top and on
              // a top bar it is underneath. inset rather than a real border: a border would shift
              // every other tab by 3px as the selection moved.
              boxShadow: active
                ? (isMobile ? 'inset 0 3px 0 var(--color-accent)' : 'inset 0 -3px 0 var(--color-accent)')
                : 'none',
              transition: 'background-color 0.2s',
              ...(isMobile
                ? {
                  flex: 1,
                  flexDirection: 'column',
                  gap: '0.15rem',
                  padding: '0.5rem 0.25rem',
                  fontSize: '0.68rem',
                  lineHeight: 1.15,
                  textAlign: 'center',
                  minWidth: 0,
                }
                : {
                  gap: '0.5rem',
                  padding: '0.85rem 1.1rem',
                  fontSize: '0.9rem',
                  whiteSpace: 'nowrap',
                }),
            }}
          >
            <Icon size={isMobile ? 20 : 18} style={{ flexShrink: 0 }} />
            {/* On a phone the label is what makes an icon guessable -- there is no hover to fall
                back on -- so it is kept and allowed to wrap rather than truncated. */}
            <span style={isMobile ? { display: 'block' } : undefined}>{m.name}</span>
          </Link>
        );
      })}
    </nav>
  );
};

export const FamilyTopBar = ({
  schoolName, logoUrl, userProfile, email, onSignOut, isMobile,
  headerActionsRef, schoolId,
}) => {
  const { t } = useT();
  return (
  <header
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      padding: isMobile ? '0.5rem 0.75rem' : '0.75rem 1.25rem',
      backgroundColor: 'var(--color-bg-card)',
      borderBottom: '1px solid var(--color-border)',
      minHeight: isMobile ? 'auto' : `${NAV_HEIGHT}px`,
      flexShrink: 0,
    }}
  >
    {logoUrl ? (
      <img
        src={logoUrl}
        alt=""
        style={{ height: isMobile ? '30px' : '36px', width: 'auto', maxWidth: '54px', objectFit: 'contain', flexShrink: 0 }}
      />
    ) : (
      <div style={{ backgroundColor: 'var(--color-primary)', color: '#fff', padding: '0.35rem', borderRadius: '7px', display: 'flex', flexShrink: 0 }}>
        <School size={isMobile ? 17 : 19} />
      </div>
    )}

    <div style={{ flex: 1, minWidth: 0 }}>
      <div
        style={{
          fontWeight: 700,
          fontSize: isMobile ? '0.92rem' : '1.02rem',
          color: 'var(--color-primary)',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {schoolName || t('login.fallbackName')}
      </div>
      {/* Who you are signed in as. Families share devices -- a parent checking on a phone their
          child also uses -- so this is not decoration; it is how somebody notices they are looking
          at the wrong account before they sign a reply slip. */}
      <div
        style={{
          fontSize: '0.72rem',
          color: 'var(--color-text-muted)',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {userProfile?.name || email}
        {userProfile?.name && userProfile?.studentId ? ` (${userProfile.studentId})` : ''}
      </div>
    </div>

    {/* Where HeaderActionsPortal parks a module's own controls on a phone. My Talent Bank has a
        person picker and filters that are desktop-sized; without this slot they render inline and
        overflow the row, which is the whole reason the portal exists. Mounted only on mobile so the
        desktop keeps rendering them in place. */}
    {isMobile && (
      <div
        ref={headerActionsRef}
        style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', justifyContent: 'flex-end', minWidth: 0, flexShrink: 0 }}
      />
    )}

    {/* Families have no sidebar, so the permanent install entry lives here. Icon-only: the top bar
        on a phone has room for very little, and it disappears entirely once installed. */}
    <InstallMenuItem compact />
    <PushToggle schoolId={schoolId} compact />


    <button
      type="button"
      onClick={onSignOut}
      className="btn-outline"
      title={t('familyNav.signOut')}
      aria-label={t('familyNav.signOut')}
      style={{
        width: 'auto',
        padding: isMobile ? '0.45rem' : '0.45rem 0.85rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.4rem',
        flexShrink: 0,
        whiteSpace: 'nowrap',
      }}
    >
      <LogOut size={16} />{!isMobile && ` ${t('familyNav.signOut')}`}
    </button>
  </header>
  );
};
